import type { UseFormReturn } from "react-hook-form"
import { parseAttributes } from "../lib/utils"
import {
	SectionConfig,
	type Attribute,
	type SECTION,
} from "../types/form-types"
import { getCommonAttributes } from "../utils/attributes-extractor"
import FieldInfoIcon from "./field-info"
import { Row } from "./row"
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "./ui/accordion"
import { Separator } from "./ui/separator"
import { useLogicStore } from "@/stores/use-conditional-logic-store"

interface SectionProps {
	section: SECTION
	form: UseFormReturn
	sectionIdx: number
}

export const Section = ({ section, form, sectionIdx }: SectionProps) => {
	const { infoIconRequired, infoIconText, infoIconType, description } =
		getCommonAttributes(section.attributes)
	const { name, isCollapsible } = getSectionAttributes(section.attributes)
	const { hiddenFields, sectionFieldCount } = useLogicStore()

	if (hiddenFields[section.section_id] || sectionFieldCount[sectionIdx] === 0)
		return null

	const SectionHeader = (
		<div className="flex flex-col justify-start space-y-1">
			<h2 className="text-sectionName flex items-center gap-1 text-start font-normal">
				{name}
				{infoIconRequired && (
					<FieldInfoIcon
						infoText={infoIconText}
						iconType={infoIconType}
					/>
				)}
			</h2>
			{description && (
				<p
					className="text-sectionDesc text-start text-sm"
					dangerouslySetInnerHTML={{ __html: description }}
				/>
			)}
		</div>
	)

	const SectionContent = (
		<div
			key={section.section_id}
			className="space-y-7"
		>
			<div className="df-section-header flex flex-col">
				<Separator className="mt-4" />
			</div>
			<div className="space-y-7 pb-8">
				{section.rows.map((row, ind) => (
					<Row
						row={row}
						key={ind}
						form={form}
					/>
				))}
			</div>
		</div>
	)

	if (isCollapsible) {
		return (
			<Accordion
				type="single"
				collapsible
				className="df-section"
				defaultValue={section.section_id} // initially open
			>
				<AccordionItem value={section.section_id}>
					<AccordionTrigger className="hover:no-underline">
						{SectionHeader}
					</AccordionTrigger>
					<AccordionContent>{SectionContent}</AccordionContent>
				</AccordionItem>
			</Accordion>
		)
	}

	// non-collapsible → just always show header + content
	return (
		<div className="df-section">
			<div className="mt-4">{SectionHeader}</div>
			<div>{SectionContent}</div>
		</div>
	)
}
function getSectionAttributes(attributes: Attribute[]) {
	const props: {
		name: string
		isCollapsible: boolean
	} = {
		name: "section",
		isCollapsible: false,
	}
	if (!attributes) return props

	const parsedAttributes = parseAttributes(attributes)

	Object.entries(parsedAttributes).forEach(([key, value]) => {
		switch (key) {
			case SectionConfig.Name:
				props.name = value as string
				break
			case SectionConfig.IsCollapsible:
				props.isCollapsible = value as boolean
				break
		}
	})

	return props
}
